'use client'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Loader2, X, Upload, FileText, AlertCircle, Download } from 'lucide-react'
import { useState, useCallback, useEffect } from 'react'
import type { DocumentFormState } from './types'
import {
  isSpreadsheetFile,
} from './types'
import { SpreadsheetPreview, type SpreadsheetPreviewData } from './spreadsheet'
import { useAuth } from '@/hooks/use-auth'
import { DocumentFileDropzone } from './document-file-dropzone'
import {
  getKnowledgeBaseUploadPath,
  validateKnowledgeBaseUploadSelection,
  type KnowledgeBaseUploadLimits,
} from '@/lib/ai/knowledge-base/upload-selection'
import { KNOWLEDGE_BASE_SOURCE_TYPE_LEARNED } from '@/lib/ai/knowledge-base/folder-types'
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"



interface DocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  formData: DocumentFormState
  onChange: (data: DocumentFormState) => void
  onSave: () => void
  onFileUpload: (files: File[], metadata: Partial<DocumentFormState>) => Promise<void>
  isSaving: boolean
  isEditing: boolean
  /** Called after successful document creation (e.g., spreadsheet) */
  onSuccess?: (data?: { agents_updated?: number }) => void
  /** Download the original source file; absent if none is stored. */
  onDownloadOriginal?: () => void
  /** Id of the document being edited — enables in-place file replace. */
  editingDocumentId?: string
  /** Replace a plain (non-spreadsheet) file-backed doc in place. */
  onReplaceFile?: (file: File) => Promise<void>
  /** Destination for a newly imported spreadsheet. */
  destinationFolderId?: string
  uploadLimits: KnowledgeBaseUploadLimits
}

export function DocumentDialog({
  open,
  onOpenChange,
  formData,
  onChange,
  onSave,
  onFileUpload,
  isSaving,
  isEditing,
  onSuccess,
  onDownloadOriginal,
  editingDocumentId,
  onReplaceFile,
  destinationFolderId,
  uploadLimits,
}: DocumentDialogProps) {
  const tUi = useTranslations("Ai.AiKnowledgeBaseDocumentDialog")
  const tCommonUi = useTranslations("Common")

  const { fetchWithAuth } = useAuth()
  // File-backed docs (uploads, spreadsheets) derive their content from the
  // original file. Editing the stored text here would desync it from the file,
  // so content is read-only — change it by re-uploading the file.
  const isFileBacked = formData.source_type === 'file'
  // The platform keeps what it learned searchable only, and never a default for other agents.
  const isLearned = formData.source_type === KNOWLEDGE_BASE_SOURCE_TYPE_LEARNED
  const [tagInput, setTagInput] = useState('')
  const [activeTab, setActiveTab] = useState<'text' | 'file'>('text')
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  const [fileError, setFileError] = useState<string | null>(null)

  // Spreadsheet-specific state
  const [spreadsheetPreview, setSpreadsheetPreview] = useState<SpreadsheetPreviewData | null>(null)
  const [isLoadingPreview, setIsLoadingPreview] = useState(false)
  const [isConfirmingSpreadsheet, setIsConfirmingSpreadsheet] = useState(false)
  // In-place replace of a plain (non-spreadsheet) file-backed doc.
  const [isReplacing, setIsReplacing] = useState(false)
  const canReplaceFile = isEditing && isFileBacked && !!editingDocumentId && !!onReplaceFile
  const uploadedFile = uploadedFiles[0] ?? null
  const isBatchUpload = uploadedFiles.length > 1
  const canSaveFile = uploadedFiles.length > 0
    && !spreadsheetPreview
    && (isBatchUpload || formData.title.trim() !== '')
  const isUploadBlocked = uploadLimits.maxFiles === 0 || uploadLimits.maxBytes === 0

  useEffect(() => {
    if (!open) {
      const timer = setTimeout(() => {
        setUploadedFiles([])
        setFileError(null)
        setActiveTab('text')
        setTagInput('')
        setSpreadsheetPreview(null)
        setIsLoadingPreview(false)
      }, 150)
      return () => clearTimeout(timer)
    }
  }, [open])

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      onChange({ ...formData, tags: [...formData.tags, tagInput.trim()] })
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    onChange({ ...formData, tags: formData.tags.filter((t) => t !== tag) })
  }

  const handleFilesSelected = useCallback(async (selectedFiles: File[]) => {
    const nextFiles = isEditing
      ? selectedFiles.slice(0, 1)
      : mergeUploadFiles(uploadedFiles, selectedFiles)

    if (!isEditing) {
      const selectionError = validateKnowledgeBaseUploadSelection(nextFiles, uploadLimits)
      if (selectionError) {
        setFileError(selectionError)
        return
      }
    }

    if (nextFiles.some(isSpreadsheetFile) && nextFiles.length > 1) {
      setFileError('Spreadsheets require a preview and must be uploaded one at a time.')
      return
    }

    setUploadedFiles(nextFiles)
    setFileError(null)
    setSpreadsheetPreview(null)

    const file = nextFiles[0]
    if (!file) return

    // Check if it's a spreadsheet file
    if (isSpreadsheetFile(file)) {
      // Upload to preview endpoint
      setIsLoadingPreview(true)
      try {
        const formDataUpload = new FormData()
        formDataUpload.append('file', file)

        const response = await fetchWithAuth('/api/ai/knowledge-base/spreadsheet/preview', {
          method: 'POST',
          body: formDataUpload,
        })

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.error || 'Failed to parse spreadsheet')
        }

        const previewData: SpreadsheetPreviewData = await response.json()
        setSpreadsheetPreview(previewData)
      } catch (error) {
        console.error('Spreadsheet preview error:', error)
        setFileError(error instanceof Error ? error.message : 'Failed to parse spreadsheet')
        setUploadedFiles([])
      } finally {
        setIsLoadingPreview(false)
      }
    } else {
      // Regular file - auto-fill title from filename if empty
      if (nextFiles.length === 1 && !formData.title) {
        onChange({ ...formData, title: file.name.replace(/\.[^/.]+$/, '') })
      }
    }
  }, [fetchWithAuth, formData, isEditing, onChange, uploadLimits, uploadedFiles])

  const handleRemoveFile = useCallback((file?: File) => {
    setUploadedFiles(currentFiles => (
      file ? currentFiles.filter(candidate => candidate !== file) : []
    ))
    setFileError(null)
    setSpreadsheetPreview(null)
  }, [])

  const handleSpreadsheetConfirm = useCallback(async (params: {
    preview_id: string
    title: string
    category: string
    ai_summary: string
    selected_sheet?: string
    injection_mode: 'auto' | 'direct' | 'rag'
  }) => {
    setIsConfirmingSpreadsheet(true)
    try {
      const response = await fetchWithAuth('/api/ai/knowledge-base/spreadsheet/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...params,
          is_default: formData.is_default,
          ...(!editingDocumentId && destinationFolderId
            ? { folder_id: destinationFolderId }
            : {}),
          // When editing a file-backed doc, replace it in place instead of creating a new one.
          ...(editingDocumentId ? { document_id: editingDocumentId } : {}),
        }),
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to save document')
      }

      const data = await response.json()

      // Success - close dialog and refresh list
      onOpenChange(false)
      onSuccess?.(data.agents_updated > 0
        ? { agents_updated: data.agents_updated }
        : undefined
      )
    } catch (error) {
      console.error('Spreadsheet confirm error:', error)
      setFileError(error instanceof Error ? error.message : 'Failed to create document')
    } finally {
      setIsConfirmingSpreadsheet(false)
    }
  }, [
    destinationFolderId,
    editingDocumentId,
    fetchWithAuth,
    formData.is_default,
    onOpenChange,
    onSuccess,
  ])

  // Replace a plain (non-spreadsheet) file in place; the page handler PUTs the file
  // and closes the dialog on success. Spreadsheets replace via the preview path above.
  const handleConfirmReplace = useCallback(async () => {
    if (!uploadedFile || !onReplaceFile) return
    setIsReplacing(true)
    try {
      await onReplaceFile(uploadedFile)
      setUploadedFiles([])
    } finally {
      setIsReplacing(false)
    }
  }, [uploadedFile, onReplaceFile])

  const handleSave = async () => {
    if (activeTab === 'file' && uploadedFiles.length > 0) {
      await onFileUpload(uploadedFiles, {
        title: isBatchUpload ? undefined : formData.title,
        category: formData.category,
        tags: formData.tags,
        injection_mode: formData.injection_mode,
        is_active: formData.is_active,
      })
    } else {
      onSave()
    }
  }

  // For spreadsheets, validation is handled by SpreadsheetPreview
  const canSave = activeTab === 'file'
    ? canSaveFile
    : formData.title.trim() !== '' && formData.content.trim() !== ''

  let saveButtonLabel = tUi("createDocument")
  if (isEditing) {
    saveButtonLabel = 'Save Changes'
  } else if (activeTab === 'file') {
    saveButtonLabel = `Upload & Create${isBatchUpload ? ` (${uploadedFiles.length})` : ''}`
  }

  let folderUploadHint = tUi("atTheKnowledgeBaseRootEachSelectedTop")
  if (destinationFolderId) {
    folderUploadHint = 'Files from selected folders and their subfolders will be flattened into the current Knowledge Base folder.'
  }
  const uploadLimitHint = getUploadLimitHint(tUi, uploadLimits)

  // If spreadsheet preview is active, show dedicated UI
  if (spreadsheetPreview) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-3xl h-[85dvh] flex flex-col"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle><span key={tUi("importSpreadsheet")}>{tUi("importSpreadsheet")}</span></DialogTitle>
            <DialogDescription>
              <span key={tUi("reviewTheParsedDataAndAIGeneratedSummary")}>{tUi("reviewTheParsedDataAndAIGeneratedSummary")}</span></DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0">
            <SpreadsheetPreview
              data={spreadsheetPreview}
              onConfirm={handleSpreadsheetConfirm}
              onCancel={handleRemoveFile}
              isConfirming={isConfirmingSpreadsheet}
            />
          </div>

          {fileError && (
            <div className="flex-shrink-0 flex items-center gap-2 text-sm text-red-600 mt-2">
              <AlertCircle className="h-4 w-4" />
              {fileError}
            </div>
          )}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90dvh] overflow-y-auto"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle><span key={isEditing ? tUi("editDocument") : tUi("addDocument")}>{isEditing ? tUi("editDocument") : tUi("addDocument")}</span></DialogTitle>
          <DialogDescription>
            <span key={isEditing
              ? tUi("updateTheDocumentContentAndSettings")
              : tUi("addANewDocumentToTheKnowledgeBase")}>{isEditing
              ? tUi("updateTheDocumentContentAndSettings")
              : tUi("addANewDocumentToTheKnowledgeBase")}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Batch uploads derive each title from its filename. */}
          {!(activeTab === 'file' && isBatchUpload) && <div className="space-y-2">
            <Label htmlFor="title"><span key={tUi("title")}>{tUi("title")}</span></Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => onChange({ ...formData, title: e.target.value })}
              placeholder={tUi("documentTitle")}
            />
          </div>}
          {activeTab === 'file' && isBatchUpload && (
            <div className="rounded-md bg-muted px-3 py-2 text-sm text-muted-foreground">
              <span key={tUi("eachDocumentTitleWillBeTakenFromIts")}>{tUi("eachDocumentTitleWillBeTakenFromIts")}</span></div>
          )}

          {/* AI Summary - shown for all documents in edit mode */}
          {isEditing && (
            <div className="space-y-2">
              <Label htmlFor="ai_summary"><span key={tUi("documentSummaryForRAG")}>{tUi("documentSummaryForRAG")}</span></Label>
              <p className="text-xs text-gray-500">
                <span key={tUi("thisSummaryIsIncludedInEachChunkWhen")}>{tUi("thisSummaryIsIncludedInEachChunkWhen")}</span></p>
              <Textarea
                id="ai_summary"
                value={formData.ai_summary || ''}
                onChange={(e) => onChange({ ...formData, ai_summary: e.target.value })}
                placeholder={tUi("briefDescriptionOfDocumentContentAndPurpose")}
                rows={10}
                className="min-h-64 text-sm sm:min-h-48"
              />
            </div>
          )}

          {/* Content Tabs - only for new documents */}
          {!isEditing ? (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'text' | 'file')}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="text" className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  <span><span key={tUi("enterText")}>{tUi("enterText")}</span></span>
                </TabsTrigger>
                <TabsTrigger value="file" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  <span><span key={tUi("uploadFiles")}>{tUi("uploadFiles")}</span></span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="text" className="space-y-2 mt-4">
                <Label htmlFor="content"><span key={tUi("content")}>{tUi("content")}</span></Label>
                <Textarea
                  id="content"
                  value={formData.content}
                  onChange={(e) => onChange({ ...formData, content: e.target.value })}
                  placeholder={tUi("documentContentSupportsMarkdown")}
                  className="min-h-[200px] font-mono text-sm"
                />
                <p className="text-xs text-gray-500">
                  {formData.content.length} <span><span key={tUi("characters")}>{tUi("characters")}</span></span>{Math.ceil(formData.content.length / 4)} <span><span key={tUi("tokens")}>{tUi("tokens")}</span></span>
                </p>
              </TabsContent>

              <TabsContent value="file" className="mt-4">
                {isLoadingPreview ? (
                  <div className="flex flex-col items-center justify-center py-12 space-y-4">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    <p className="text-sm text-gray-600"><span key={tUi("analyzingSpreadsheet")}>{tUi("analyzingSpreadsheet")}</span></p>
                  </div>
                ) : (
                  <>
                    <DocumentFileDropzone
                      onFilesSelected={(files) => void handleFilesSelected(files)}
                      files={uploadedFiles}
                      onRemoveFile={handleRemoveFile}
                      fileError={fileError}
                      onFileError={setFileError}
                      multiple
                      maxFiles={Math.max(1, uploadLimits.maxFiles)}
                      allowFolderSelection
                      disabled={isSaving || isUploadBlocked}
                    />
                    <p className="mt-3 text-xs text-muted-foreground">
                      {folderUploadHint}
                      {' '}<span><span key={tUi("spreadsheetsMustBeReviewedAndUploadedOneAt")}>{tUi("spreadsheetsMustBeReviewedAndUploadedOneAt")}</span></span>
                    </p>
                    <p className="mt-1 text-xs font-medium text-muted-foreground">
                      {uploadLimitHint}
                    </p>
                  </>
                )}
              </TabsContent>
            </Tabs>
          ) : isFileBacked ? (
            /* File-backed doc: content is derived from the original file — read-only */
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="content"><span key={tUi("content2")}>{tUi("content2")}</span></Label>
                {onDownloadOriginal && (
                  <button
                    type="button"
                    onClick={onDownloadOriginal}
                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span><span key={tUi("downloadOriginalFile")}>{tUi("downloadOriginalFile")}</span></span>
                  </button>
                )}
              </div>
              <Textarea
                id="content"
                value={formData.content}
                readOnly
                className="min-h-[200px] font-mono text-sm bg-muted text-muted-foreground cursor-not-allowed"
              />
              <p className="flex items-start gap-1.5 text-xs text-amber-600">
                <AlertCircle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                <span><TranslationText>{tUi.rich("thisDocumentSContentComesFromTheUploaded", { value0: String(canReplaceFile
                    ? tUi("uploadTheUpdatedFileBelowToReplaceIt")
                    : tUi("deleteThisDocumentAndUploadTheFileAgain")), tag0: () => (onDownloadOriginal && (
                    <>
                      <TranslationText>{tUi.rich("editOriginalPrompt", {download: chunks => (
                        <button type="button" onClick={onDownloadOriginal}
                          className="font-medium text-blue-600 hover:text-blue-800 hover:underline">
                          <TranslationText>{chunks}</TranslationText>
                        </button>
                      )})}</TranslationText>
                    </>
                  )) })}</TranslationText></span>
              </p>

              {canReplaceFile && (
                <div className="space-y-2 rounded-md border border-dashed p-3">
                  <Label><span key={tUi("replaceFile")}>{tUi("replaceFile")}</span></Label>
                  {isLoadingPreview ? (
                    <div className="flex items-center gap-2 py-4 text-sm text-gray-600">
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                      <span><span key={tUi("analyzingSpreadsheet")}>{tUi("analyzingSpreadsheet")}</span></span>
                    </div>
                  ) : (
                    <DocumentFileDropzone
                      onFilesSelected={(files) => void handleFilesSelected(files)}
                      files={uploadedFiles}
                      onRemoveFile={handleRemoveFile}
                      fileError={fileError}
                      onFileError={setFileError}
                    />
                  )}
                  {uploadedFile && !spreadsheetPreview && !isLoadingPreview && (
                    <Button
                      type="button"
                      onClick={handleConfirmReplace}
                      disabled={isReplacing}
                      className="w-full"
                    >
                      {isReplacing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      <span><span key={tUi("replaceDocument")}>{tUi("replaceDocument")}</span></span>
                    </Button>
                  )}
                </div>
              )}
            </div>
          ) : (
            /* Content textarea for editing */
            <div className="space-y-2">
              <Label htmlFor="content"><span key={tUi("content")}>{tUi("content")}</span></Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => onChange({ ...formData, content: e.target.value })}
                placeholder={tUi("documentContentSupportsMarkdown")}
                className="min-h-[200px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500">
                {formData.content.length} <span><span key={tUi("characters")}>{tUi("characters")}</span></span>{Math.ceil(formData.content.length / 4)} <span><span key={tUi("tokens")}>{tUi("tokens")}</span></span>
              </p>
            </div>
          )}

          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category"><span key={tUi("category")}>{tUi("category")}</span></Label>
            <Input
              id="category"
              value={formData.category}
              onChange={(e) => onChange({ ...formData, category: e.target.value })}
              placeholder={tUi("eGFAQPoliciesProducts")}
            />
          </div>

          {/* Tags isn't using now*/}
          { false && (<div className="space-y-2">
            <Label><span key={tUi("tags")}>{tUi("tags")}</span></Label>
            <div className="flex gap-2">
              <Input
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
                placeholder={tUi("addTagAndPressEnter")}
              />
              <Button type="button" variant="outline" onClick={handleAddTag}>
                <span key={tUi("add")}>{tUi("add")}</span></Button>
            </div>
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="pr-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 hover:text-red-600"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Storage Mode */}
          <div className="space-y-2">
            <Label htmlFor="injection_mode"><span key={tUi("storageMode")}>{tUi("storageMode")}</span></Label>
            {isLearned && (
              <p className="text-xs text-gray-500"><span key={tUi("learnedDocumentsAreLookedUpWhenAQuestion")}>{tUi("learnedDocumentsAreLookedUpWhenAQuestion")}</span></p>
            )}
            <Select
              disabled={isLearned}
              value={formData.injection_mode}
              onValueChange={(value) =>
                onChange({ ...formData, injection_mode: value as 'direct' | 'rag' | 'auto' })
              }
            >
              <SelectTrigger className="w-full max-w-xs">
                <SelectValue placeholder={tUi("selectMode")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto"><span key={tUi("autoRecommended")}>{tUi("autoRecommended")}</span></SelectItem>
                <SelectItem value="direct"><span key={tUi("directInjection")}>{tUi("directInjection")}</span></SelectItem>
                <SelectItem value="rag"><span key={tUi("rAGRetrieval")}>{tUi("rAGRetrieval")}</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Priority (only for direct injection) */}
          {formData.injection_mode === 'direct' && (
            <div className="space-y-2">
              <Label htmlFor="priority"><span key={tUi("priority11000LowerHigherPriority")}>{tUi("priority11000LowerHigherPriority")}</span></Label>
              <Input
                id="priority"
                type="number"
                min={1}
                max={1000}
                value={formData.priority}
                onChange={(e) =>
                  onChange({ ...formData, priority: parseInt(e.target.value) || 100 })
                }
              />
            </div>
          )}

          {/* Active */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is_active"><span key={tUi("active")}>{tUi("active")}</span></Label>
              <p className="text-xs text-gray-500">
                <span key={tUi("inactiveDocumentsAreNotUsedByAI")}>{tUi("inactiveDocumentsAreNotUsedByAI")}</span></p>
            </div>
            <Switch
              id="is_active"
              checked={formData.is_active}
              onCheckedChange={(checked) => onChange({ ...formData, is_active: checked })}
            />
          </div>

          {/* Default for all agents */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is_default"><span key={tUi("defaultForAllAgents")}>{tUi("defaultForAllAgents")}</span></Label>
              <p key={isEditing
                  ? tUi("newAgentsWillAutoIncludeThisDocument")
                  : tUi("autoApplyToAllExistingAgentsAndInclude")} className="text-xs text-gray-500">
                {isEditing
                  ? tUi("newAgentsWillAutoIncludeThisDocument")
                  : tUi("autoApplyToAllExistingAgentsAndInclude")}
              </p>
            </div>
            <Switch
              id="is_default"
              disabled={isLearned}
              checked={formData.is_default}
              onCheckedChange={(checked) => onChange({ ...formData, is_default: checked })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !canSave}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {saveButtonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function mergeUploadFiles(currentFiles: File[], selectedFiles: File[]): File[] {
  const filesByKey = new Map<string, File>()
  for (const file of [...currentFiles, ...selectedFiles]) {
    const relativePath = getKnowledgeBaseUploadPath(file)
    filesByKey.set(
      `${relativePath}:${file.name}:${file.size}:${file.lastModified}`,
      file,
    )
  }
  return [...filesByKey.values()]
}

function getUploadLimitHint(tUi: ReturnType<typeof useTranslations<"Ai.AiKnowledgeBaseDocumentDialog">>, limits: KnowledgeBaseUploadLimits): string {
  const documentLabel = tUi("value0DocumentValue1", { value0: String(limits.maxFiles), value1: String(limits.maxFiles === 1 ? '' : 's') })
  if (limits.maxBytes === null) {
    return tUi("currentBatchLimitValue0KnowledgeBaseStorageIs", { value0: String(documentLabel) })
  }

  return tUi("currentRemainingAllowanceValue0AndValue1OfKnowledge", { value0: String(documentLabel), value1: String(formatBytes(tUi, limits.maxBytes)) })
}

function formatBytes(tUi: ReturnType<typeof useTranslations<"Ai.AiKnowledgeBaseDocumentDialog">>, bytes: number): string {
  const bytesPerMegabyte = 1024 * 1024
  if (bytes < bytesPerMegabyte) return tUi("value0KB", { value0: String((bytes / 1024).toFixed(1)) })
  return tUi("value0MB", { value0: String((bytes / bytesPerMegabyte).toFixed(1)) })
}
